import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { Data, DatabaseMatch, Match, Matches, TeamGameMatch, TeamGameMatchTeam, TournamentConfig } from '../../database.schema';
import { AWAITING_SCORES, TOURNAMENT_STATE_ARCHIVED } from '../app/constants';
import { getTourGroup } from '../utils/get-tour-group';

const fetchData = async (): Promise<Data> => {
  const tourGroup = getTourGroup();
  var dbDNS = `https://golf-tour-462cf-default-rtdb.europe-west1.firebasedatabase.app/.json`;
  if (process.env.REACT_APP_MODE === 'dev') {
    dbDNS = `http://127.0.0.1:9000/.json?ns=golf-tour-462cf-default-rtdb`;
  }
  const response = await fetch(dbDNS);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  const json = await response.json();
  const returnData = json.tournaments[tourGroup] ? [...json.tournaments[tourGroup]] : null;
  const tourData = { ...json, tournaments: returnData };
  return { ...tourData };
};

export const useFetchData = (select: (data: Data) => unknown) =>
  useQuery({
    queryKey: ['all-data'],
    queryFn: fetchData,
    select,
    staleTime: Infinity,
  });

type ResultsData = {
  courseId: number;
  matches: DatabaseMatch[];
  resultIndex: number;
  resultsFormat: string;
  selectedTournamentIndex: number;
  startingHole: number;
};

const mutateResults = async (resultData: ResultsData): Promise<ResultData> => {
  const authToken = Cookies.get('token');
  const tourGroup = getTourGroup();
  const { selectedTournamentIndex, resultIndex, matches, courseId, startingHole, resultsFormat } = resultData;
  var dbDNS = `https://golf-tour-462cf-default-rtdb.europe-west1.firebasedatabase.app/tournaments/${tourGroup}/${selectedTournamentIndex}/ryderCup/results/${resultIndex}/.json?auth=${authToken}`;
  if (process.env.REACT_APP_MODE === 'dev') {
    dbDNS = `http://127.0.0.1:9000/tournaments/${tourGroup}/${selectedTournamentIndex}/ryderCup/results/${resultIndex}/.json?ns=golf-tour-462cf-default-rtdb&auth=${authToken}`;
  }

  const response = await fetch(dbDNS, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      day: resultIndex + 1,
      courseId,
      startingHole,
      format: resultsFormat,
      matches,
    }),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return await response.json();
};

export const useTournamentResultsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mutateResults,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['all-data'] });
    },
  });
};

type ResultData = {
  selectedTournamentIndex: number;
  resultIndex: number;
  matches: Matches;
};

const mutateCreateMatch = async (resultData: ResultData): Promise<ResultData> => {
  const { selectedTournamentIndex, resultIndex, matches } = resultData;
  const tourGroup = getTourGroup();
  const authToken = Cookies.get('token');
  var dbDNS = `https://golf-tour-462cf-default-rtdb.europe-west1.firebasedatabase.app/tournaments/${tourGroup}/${selectedTournamentIndex}/ryderCup/results/${resultIndex}/.json?auth=${authToken}`;
  if (process.env.REACT_APP_MODE === 'dev') {
    dbDNS = `http://127.0.0.1:9000/tournaments/${tourGroup}/${selectedTournamentIndex}/ryderCup/results/${resultIndex}/.json?ns=golf-tour-462cf-default-rtdb&auth=${authToken}`;
  }
  const response = await fetch(dbDNS, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status: AWAITING_SCORES, day: resultIndex + 1, ...matches }),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return await response.json();
};

export const useCreateMatchMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mutateCreateMatch,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['all-data'] });
    },
  });
};

type ResultScoreCardData = {
  selectedTournamentIndex: number;
  resultIndex: number;
  matchIndex: number;
  recordedMatch: Match;
};

const mutateResultScoreCard = async (resultData: ResultScoreCardData): Promise<ResultScoreCardData> => {
  const { selectedTournamentIndex, resultIndex, recordedMatch, matchIndex } = resultData;
  const tourGroup = getTourGroup();
  const authToken = Cookies.get('token');
  var dbDNS = `https://golf-tour-462cf-default-rtdb.europe-west1.firebasedatabase.app/tournaments/${tourGroup}/${selectedTournamentIndex}/ryderCup/results/${resultIndex}/matches/${matchIndex}/.json?auth=${authToken}`;
  if (process.env.REACT_APP_MODE === 'dev') {
    dbDNS = `http://127.0.0.1:9000/tournaments/${tourGroup}/${selectedTournamentIndex}/ryderCup/results/${resultIndex}/matches/${matchIndex}/.json?ns=golf-tour-462cf-default-rtdb&auth=${authToken}`;
  }
  const response = await fetch(dbDNS, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...recordedMatch }),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return await response.json();
};

export const useTournamentResultScoreCardMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mutateResultScoreCard,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['all-data'] });
    },
  });
};

type TierPointsResultScoreCardData = {
  selectedTournamentIndex: number;
  teamIndex: number;
  matchIndex: number;
  recordedMatchTeam: TeamGameMatchTeam;
};

const mutateTierPointsResultScoreCard = async (resultData: TierPointsResultScoreCardData): Promise<TierPointsResultScoreCardData> => {
  const { selectedTournamentIndex, teamIndex, recordedMatchTeam, matchIndex } = resultData;
  const tourGroup = getTourGroup();
  const authToken = Cookies.get('token');
  var dbDNS = `https://golf-tour-462cf-default-rtdb.europe-west1.firebasedatabase.app/tournaments/${tourGroup}/${selectedTournamentIndex}/teamGames/matches/${matchIndex}/teams/${teamIndex}/.json?auth=${authToken}`;
  if (process.env.REACT_APP_MODE === 'dev') {
    dbDNS = `http://127.0.0.1:9000/tournaments/${tourGroup}/${selectedTournamentIndex}/teamGames/matches/${matchIndex}/teams/${teamIndex}/.json?ns=golf-tour-462cf-default-rtdb&auth=${authToken}`;
  }

  const response = await fetch(dbDNS, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...recordedMatchTeam, scoresEntered: true }),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return await response.json();
};

export const useTournamentTierPointsResultScoreCardMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mutateTierPointsResultScoreCard,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['all-data'] });
    },
  });
};

type TeamGameMatchPlayResultScoreCardData = {
  selectedTournamentIndex: number;
  matchIndex: number;
  recordedMatchTeams: Array<TeamGameMatchTeam>;
  teamGameMatch: TeamGameMatch;
};

const mutateTeamGameMatchPlayResultScoreCard = async (resultData: TeamGameMatchPlayResultScoreCardData): Promise<TeamGameMatchPlayResultScoreCardData> => {
  const { selectedTournamentIndex, recordedMatchTeams, matchIndex, teamGameMatch } = resultData;
  const tourGroup = getTourGroup();
  const authToken = Cookies.get('token');
  var dbDNS = 'https://golf-tour-462cf-default-rtdb.europe-west1.firebasedatabase.app';
  if (process.env.REACT_APP_MODE === 'dev') {
    dbDNS = 'http://127.0.0.1:9000';
  }
  const dbDNSTeamsPath = `${dbDNS}/tournaments/${tourGroup}/${selectedTournamentIndex}/teamGames/matches/${matchIndex}/teams/.json?ns=golf-tour-462cf-default-rtdb&auth=${authToken}`;
  const response = await fetch(dbDNSTeamsPath, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify([...recordedMatchTeams]),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  const updateResponse = await response.json();

  const dbDNSMatchesPath = `${dbDNS}/tournaments/${tourGroup}/${selectedTournamentIndex}/teamGames/matches/${matchIndex}/matchPlayMatches/.json?ns=golf-tour-462cf-default-rtdb&auth=${authToken}`;
  const matchesResponse = await fetch(dbDNSMatchesPath, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify([...(teamGameMatch?.matchPlayMatches ?? [])]),
  });

  return await matchesResponse.json();
};

export const useTournamentTeamGameMatchPlayResultScoreCardMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mutateTeamGameMatchPlayResultScoreCard,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['all-data'] });
    },
  });
};

export type BaseTournamentData = {
  selectedTournamentIndex: number;
  state: string;
  type: string;
  config: TournamentConfig;
  year: number;
};

const mutateTournament = async (tournamentData: BaseTournamentData): Promise<BaseTournamentData> => {
  const { selectedTournamentIndex, ...data } = tournamentData;
  const tourGroup = getTourGroup();
  const authToken = Cookies.get('token');
  var dbDNS = `https://golf-tour-462cf-default-rtdb.europe-west1.firebasedatabase.app/tournaments/${tourGroup}/${selectedTournamentIndex}/.json?auth=${authToken}`;
  if (process.env.REACT_APP_MODE === 'dev') {
    dbDNS = `http://127.0.0.1:9000/tournaments/${tourGroup}/${selectedTournamentIndex}/.json?ns=golf-tour-462cf-default-rtdb&auth=${authToken}`;
  }
  const response = await fetch(dbDNS, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...data }),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return await response.json();
};

export const useTournamentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mutateTournament,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['all-data'] });
    },
  });
};

const mutateArchiveTournament = async (selectedTournamentIndex: number): Promise<number> => {
  const tourGroup = getTourGroup();
  const authToken = Cookies.get('token');
  var dbDNS = `https://golf-tour-462cf-default-rtdb.europe-west1.firebasedatabase.app/tournaments/${tourGroup}/${selectedTournamentIndex}/state.json?auth=${authToken}`;
  if (process.env.REACT_APP_MODE === 'dev') {
    dbDNS = `http://127.0.0.1:9000/tournaments/${tourGroup}/${selectedTournamentIndex}/.json?ns=golf-tour-462cf-default-rtdb&auth=${authToken}`;
  }
  const response = await fetch(dbDNS, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ state: TOURNAMENT_STATE_ARCHIVED }),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return await response.json();
};

export const useTournamentArciveMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mutateArchiveTournament,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['all-data'] });
    },
  });
};
